import React from 'react';
import Bolt from '@mui/icons-material/Bolt';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import BCIImage from '../../constants/bci-image';
import { InvoiceType } from '../../propTypes';
import { LIGHTNING_PAYMENT_STATE } from '../../constants/cache-constants';
import { getLightningPaymentTooltip } from '../../helpers/lightning-payment-tooltip';
import ONBOARDING_TOOLTIP_CLASS_NAME from '../../theme/constants/tooltip';
import LazyLoadImage from '../images/LazyLoadImage';

const VISUAL_TYPE = {
  ICON: 'bolt',
  IMAGE: 'image',
};

const DEFAULT_FAST_PAYMENT_VISUAL = VISUAL_TYPE.ICON;

const FAST_PAYMENT_VISUAL_BY_CODE = {
  [LIGHTNING_PAYMENT_STATE.STANDARD]: VISUAL_TYPE.ICON,
  [LIGHTNING_PAYMENT_STATE.BCI]: VISUAL_TYPE.IMAGE,
};

const DEFAULT_ICON_SIZE = 24;

const getFastPaymentVisualType = (statusCode) => (
  FAST_PAYMENT_VISUAL_BY_CODE[String(statusCode)] ?? DEFAULT_FAST_PAYMENT_VISUAL
);

const FastPaymentTitle = ({ invoice }) => {
  const tooltip = getLightningPaymentTooltip(invoice);

  if (!tooltip) return null;

  if (!tooltip.title) {
    return <Typography variant="body2">{tooltip.text}</Typography>;
  }

  return (
    <>
      <Typography variant="caption">{tooltip.subtitle}</Typography>
      <Typography variant="body2">{tooltip.title}</Typography>
    </>
  );
};

const LightningFastPayment = ({ invoice, iconSize, ...props }) => {
  const visualType = getFastPaymentVisualType(invoice.lightningPaymentState);
  const tooltip = getLightningPaymentTooltip(invoice);

  if (!tooltip) return null;

  return (
    <Tooltip
      title={<FastPaymentTitle invoice={invoice} />}
      classes={{ tooltip: ONBOARDING_TOOLTIP_CLASS_NAME }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: 0.1,
          lineHeight: 0,
        }}
      >
        {visualType === VISUAL_TYPE.ICON ? (
          <Bolt color="success" sx={{ width: iconSize, height: iconSize }} {...props} />
        ) : (
          <LazyLoadImage
            src={BCIImage}
            alt=""
            loaderSize={iconSize}
            sx={{
              width: { xs: iconSize / 1.5, md: iconSize },
              height: { xs: iconSize / 1.5, md: iconSize },
            }}
            {...props}
          />
        )}
      </Box>
    </Tooltip>
  );
};

FastPaymentTitle.propTypes = {
  invoice: InvoiceType.isRequired,
};

LightningFastPayment.propTypes = {
  invoice: InvoiceType.isRequired,
  iconSize: PropTypes.number,
};

LightningFastPayment.defaultProps = {
  iconSize: DEFAULT_ICON_SIZE,
};

export default LightningFastPayment;
